import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { format } from 'date-fns';
import type { DeferredMessageRow } from './DeferredMessages';
import './IHubAdapter.scss';

interface DeferredMessageDetailsProps {
  open: boolean;
  message: DeferredMessageRow | null;
  onClose: () => void;
}

type PillVariant = 'success' | 'warning' | 'danger' | 'neutral';

const REASON_META: Record<string, { label: string; variant: PillVariant }> = {
  MISSING_SERVICE: { label: 'Missing service', variant: 'danger' },
  CONFLICT: { label: 'Conflict', variant: 'warning' },
  OTHER: { label: 'Other', variant: 'neutral' },
};

const fmtDate = (value: string | null): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : format(parsed, 'dd MMM yyyy, HH:mm:ss');
};

const asText = (value: unknown): string =>
  value === null || value === undefined || value === '' ? '-' : String(value);

const DeferredMessageDetails = ({ open, message, onClose }: DeferredMessageDetailsProps) => {
  const reason = message?.deferredReason
    ? (REASON_META[message.deferredReason] ?? {
        label: message.deferredReason,
        variant: 'neutral' as PillVariant,
      })
    : null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ className: 'ihub-dm-detail-paper' }}
    >
      <DialogTitle className="ihub-dm-detail-title">
        <Box>
          <Typography variant="h6" className="ihub-dm-detail-heading">
            Deferred message
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {asText(message?.serviceId)}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent className="ihub-dm-detail-content">
        {message && (
          <>
            <div className="ihub-dm-detail-pills">
              <span className={`ihub-pill ihub-pill-${message.processed ? 'success' : 'warning'}`}>
                {message.processed ? 'Processed' : 'Pending'}
              </span>
              <span className={`ihub-pill ihub-pill-${message.expired ? 'danger' : 'neutral'}`}>
                {message.expired ? 'Expired' : 'Valid'}
              </span>
              {reason && (
                <span className={`ihub-pill ihub-pill-${reason.variant}`}>{reason.label}</span>
              )}
            </div>

            <dl className="ihub-dm-detail-grid">
              <dt>Service ID</dt>
              <dd className="ihub-dm-detail-mono">{asText(message.serviceId)}</dd>
              <dt>Message ID</dt>
              <dd className="ihub-dm-detail-mono">{asText(message.messageId)}</dd>
              <dt>Sequence #</dt>
              <dd className="ihub-dm-detail-mono">{asText(message.sequenceNumber)}</dd>
              <dt>Event type</dt>
              <dd>{asText(message.eventType)}</dd>
              <dt>Message type</dt>
              <dd>{asText(message.messageType)}</dd>
              <dt>Error code</dt>
              <dd>{asText(message.errorCode)}</dd>
              <dt>Created</dt>
              <dd>{fmtDate(message.createdDate)}</dd>
              <dt>Processed at</dt>
              <dd>{fmtDate(message.processedDate)}</dd>
            </dl>

            <div className="ihub-dm-detail-desc-block">
              <span className="ihub-dm-detail-desc-label">Details</span>
              <p className="ihub-dm-detail-desc">{asText(message.description)}</p>
            </div>
          </>
        )}
      </DialogContent>

      <Divider />

      <DialogActions className="ihub-dm-detail-actions">
        <Button variant="outlined" onClick={onClose} className="ihub-dm-detail-close-btn">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeferredMessageDetails;
